import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plane, Languages } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/10611221/pexels-photo-10611221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      toast.error(t("invalidCredentials"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-5" data-testid="login-page">
      {/* Hero panel */}
      <div className="hidden lg:col-span-3 lg:flex relative overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A192F]/55" />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#D4AF37] text-[#0A192F] grid place-items-center">
              <Plane size={22} />
            </div>
            <div className="display-font text-xl font-semibold">{t("appName")}</div>
          </div>
          <div className="max-w-lg fade-up">
            <div className="uppercase-label text-white/70 mb-3">CRM · Bookings · PDF</div>
            <h1 className="display-font text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              {t("tagline")}
            </h1>
            <div className="mt-6 divider-accent" />
          </div>
          <div className="text-white/60 text-sm">© {new Date().getFullYear()} · {t("appName")}</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="lg:col-span-2 flex items-center justify-center px-6 py-16 bg-[hsl(var(--background))]">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="w-9 h-9 rounded-lg bg-[#0A192F] text-white grid place-items-center">
                <Plane size={18} />
              </div>
              <div className="display-font font-semibold">{t("appName")}</div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-testid="lang-toggle-btn"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="ms-auto"
            >
              <Languages size={16} className="me-2" />
              {lang === "ar" ? "English" : "العربية"}
            </Button>
          </div>

          <div className="mb-8">
            <div className="uppercase-label mb-2">{t("signIn")}</div>
            <h2 className="display-font text-3xl font-bold text-[hsl(var(--primary))]">
              {t("loginWelcome")}
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
              {t("loginSubtitle")}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                placeholder="you@example.com"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-[#0A192F] hover:bg-[#112240] text-white active:scale-[0.98]"
            >
              {busy ? t("loading") : t("signIn")}
            </Button>
          </form>

          <Card className="mt-8 p-4 bg-[hsl(var(--secondary))] border-dashed">
            <div className="uppercase-label mb-1">Demo</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              admin@travelcrm.com · Admin@12345
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
